import AxiosInstance from "@/api/axios";
import { GeneralFilterType, QuestionType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

let abortController: AbortController | null = null;

interface TopQuestionFilterType extends GeneralFilterType {
  region_id?: string;
}

export const fetchTopQuestions = createAsyncThunk(
  "getTopQuestions/fetchData",
  async (
    {
      organization_id,
      agent,
      date_range,
      branch_id,
      region_id
    }: TopQuestionFilterType,
    { getState, rejectWithValue }
  ) => {
    if (!branch_id && !region_id) return null;
    if (branch_id === "all") return null;
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();

    try {
      const state = getState() as { topQuestions: TopQuestionsType };
      const { lastFetchedParams, data } = state.topQuestions;

      const paramsChanged =
        !data ||
        lastFetchedParams?.organization_id !== organization_id ||
        lastFetchedParams?.branch_id !== branch_id ||
        lastFetchedParams?.region_id !== region_id ||
        lastFetchedParams?.agent !== agent ||
        lastFetchedParams?.date_range !== date_range;

      if (!paramsChanged) {
        return null;
      }

      const intentsArray = [
        "Membership and Joining",
        "Membership Management",
        "Payments and Finances",
        "Freezing and Cancellations",
        "Facilities and Amenities",
        "Classes and Training",
        "Feedback and Complaints",
        "Membership Agreement",
        "Rules, Class Schedule",
        "offers"
      ];
      const intentsQuery = intentsArray
        .map((intent) => `intents=${encodeURIComponent(intent)}`)
        .join("&");
      const response = await AxiosInstance.get(
        `/queries/top_queries_intent?${intentsQuery}`,
        {
          params: {
            organization_id,
            branch_id,
            region_id,
            agents: agent,
            date_range
          },
          signal: abortController.signal
        }
      );

      if (response.data.success) {
        return {
          data: response.data.data,
          params: { organization_id, branch_id, agent, date_range, region_id }
        };
      } else {
        return null;
      }
    } catch (error: any) {
      if (abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return rejectWithValue(error);
    } finally {
      abortController = null;
    }
  }
);

interface TopQuestionsType {
  data: QuestionType | null | undefined;
  loading: boolean;
  error: string | null;
  lastFetchedParams: TopQuestionFilterType | null;
}

const initialState: TopQuestionsType = {
  data: null,
  loading: false,
  error: null,
  lastFetchedParams: null
};

const topQuestionsReducer = createSlice({
  name: "topQuestions",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchTopQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopQuestions.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.data = action.payload.data;
          state.lastFetchedParams = action.payload.params;
        }
      })
      .addCase(fetchTopQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export default topQuestionsReducer.reducer;
